import { Outlet, Link, useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Sidebar from '@/components/Sidebar'
import Navbar from '@/components/Navbar'
import BottomNav from '@/components/BottomNav'
import ErrorBoundary from '@/components/ErrorBoundary'
import { OfflineBanner } from '@/components/OfflineBanner'
import EmergencyPanel from '@/components/EmergencyPanel'
import QuickLogFAB from '@/components/QuickLogFAB'
import FaunaReportFAB from '@/components/FaunaReportFAB'
import SessionManager from '@/components/SessionManager'
import OfflineSyncManager from '@/components/OfflineSyncManager'
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { profileApi } from '@/api/profile'
import { useAuthStore } from '@/stores/auth.store'
import OnboardingWizard, { isOnboardingDone } from '@/pages/onboarding/OnboardingWizard'
import i18n from '@/i18n'
import Icon from '@/components/ui/Icon'
import { IcTriangleAlert } from '@/components/ui/icons'

const PROFILE_INCOMPLETE_KEY = 'edive-profile-incomplete-dismissed'

function ProfileCompletionBanner({ onDismiss }: { onDismiss: () => void }) {
  const { t } = useTranslation()
  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-4 text-sm">
      <div className="flex items-center gap-2 text-amber-800 min-w-0">
        <span className="text-base shrink-0"><Icon icon={IcTriangleAlert} size="sm" /></span>
        <span className="truncate">
          <strong>{t('layout.profileIncompleteTitle')}</strong>{' '}
          {t('layout.profileIncompleteBody')}{' '}
          <Link to="/app/profile" className="underline font-medium hover:text-amber-900">
            {t('layout.updateNow')}
          </Link>
        </span>
      </div>
      <button
        onClick={onDismiss}
        aria-label={t('layout.close')}
        className="shrink-0 text-amber-600 hover:text-amber-800 text-lg leading-none"
      >
        ×
      </button>
    </div>
  )
}

function isProfileIncomplete(profile: { mobilePhone?: string | null; dateOfBirth?: string | null } | undefined): boolean {
  if (!profile) return false
  return !profile.mobilePhone || !profile.dateOfBirth
}

export default function AppLayout() {
  const { pathname } = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem(PROFILE_INCOMPLETE_KEY) === '1'
  )

  const hasCapability = useAuthStore(s => s.hasCapability)
  const profileType   = useAuthStore(s => s.profileType)
  const setProfileType = useAuthStore(s => s.setProfileType)
  const isDiver = hasCapability('DIVER') || hasCapability('INSTRUCTOR') || hasCapability('DIVEMASTER') ||
    ['DIVER', 'INSTRUCTOR', 'DIVEMASTER'].includes(profileType ?? '')

  const { data: profile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: profileApi.getMyProfile,
    staleTime: 5 * 60_000,
    retry: false,
  })

  const { data: overdueEquipment = [] } = useQuery({
    queryKey: ['equipment', 'overdue'],
    queryFn: profileApi.overdueEquipment,
    enabled: isDiver,
    staleTime: 10 * 60_000,
    retry: false,
  })

  // Il tema scuro per le classi grezze (styles/dark-compat.css) vale solo dentro l'app: le pagine
  // pubbliche hanno un tema scuro proprio. L'attributo sta su <html> perche' deve raggiungere anche
  // i portali (finestre, menu) che React monta fuori da questo layout.
  useEffect(() => {
    document.documentElement.setAttribute('data-surface', 'app')
    return () => document.documentElement.removeAttribute('data-surface')
  }, [])

  useEffect(() => {
    // Admin can force wizard re-run by navigating to any /app/* route with ?reset-onboarding=1
    if (searchParams.get('reset-onboarding') === '1') {
      localStorage.removeItem('edive_onboarding_done')
      localStorage.removeItem('edive_jtbd_jobs')
      setSearchParams((p) => { p.delete('reset-onboarding'); return p }, { replace: true })
      setShowOnboarding(true)
      return
    }
  }, [searchParams])

  useEffect(() => {
    if (!profile) return
    // Sync profileType to auth store in case login's profile fetch failed
    if (profile.profileType && profile.profileType !== profileType) {
      setProfileType(profile.profileType as any)
    }
    if (isOnboardingDone()) return
    // Show wizard if user has no displayName (fresh account)
    if (!profile.displayName) setShowOnboarding(true)
  }, [profile])

  useEffect(() => {
    if (!profile?.locale) return
    // Only apply profile locale when no explicit user preference is saved in this browser.
    // If the user has manually switched language, localStorage takes priority.
    const saved = localStorage.getItem('edive-lang')
    if (!saved && i18n.language !== profile.locale) {
      i18n.changeLanguage(profile.locale)
      localStorage.setItem('edive-lang', profile.locale)
    }
  }, [profile?.locale])

  const showProfileBanner = !bannerDismissed && isProfileIncomplete(profile)

  function dismissBanner() {
    sessionStorage.setItem(PROFILE_INCOMPLETE_KEY, '1')
    setBannerDismissed(true)
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <OfflineBanner />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Navbar onMenuClick={() => setSidebarOpen(true)} />
        {showProfileBanner && <ProfileCompletionBanner onDismiss={dismissBanner} />}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 pb-36 lg:pb-6">
          <ErrorBoundary key={pathname}>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>

      <BottomNav onMenuClick={() => setSidebarOpen(true)} overdueCount={overdueEquipment.length} />
      <EmergencyPanel />
      {pathname.startsWith('/app/species')
        ? <FaunaReportFAB />
        : isDiver && (pathname === '/app/dashboard' || pathname === '/app/logbook' || pathname.startsWith('/app/divesites'))
          ? <QuickLogFAB />
          : null}
      {showOnboarding && <OnboardingWizard />}
      <SessionManager />
      <OfflineSyncManager />
    </div>
  )
}
